import type { MinimalGlossaryTerm } from "@/app/(marketing)/(payload)/dictionary/_actions/glossary";
import { GlossaryText } from "@/components/common/glossary-text";
import { Card, CardContent } from "@/components/ui/card";
import {
  formatFundingMoney,
  type FundingEstimate,
} from "@/lib/calculators/acquisition-funding";

export function FundingResults({
  result,
  currency,
  compact,
  terms = [],
}: {
  result: FundingEstimate | null;
  currency: string;
  compact: boolean;
  terms?: MinimalGlossaryTerm[];
}) {
  const money = (n: number, decimals?: number) =>
    formatFundingMoney(n, currency, decimals);
  if (!result)
    return (
      <Card className="border-border bg-muted/30 shadow-none">
        <CardContent className="p-6">
          <h3 className="text-xl font-medium">Check your inputs</h3>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Fix the highlighted fields to see your cash requirement and loan
            payments. No estimate is shown for an invalid scenario.
          </p>
        </CardContent>
      </Card>
    );
  const { input, bank, seller } = result;
  const funding = [
    {
      label: "Your cash",
      amount: result.buyerCash,
      color: "fill-[var(--venturu-palette-pine)]",
    },
    {
      label: "Lender finance",
      amount: input.bankAmount,
      color: "fill-[var(--venturu-palette-green-ink)]",
    },
    {
      label: "Seller financing",
      amount: input.sellerAmount,
      color: "fill-[var(--venturu-palette-mint)]",
    },
    {
      label: "Partner equity",
      amount: input.partnerEquity,
      color: "fill-[var(--venturu-palette-light-border)]",
    },
  ];
  return (
    <div className="space-y-6">
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        Estimated cash needed {money(result.buyerCash)}. Initial monthly loan
        payments {money(result.monthly, 2)}.
      </output>
      <Card className="overflow-hidden border-border shadow-none">
        <CardContent className="p-0">
          <div className="bg-[var(--venturu-palette-pine)] p-6 text-[var(--venturu-palette-paper)] sm:p-8">
            <h3 className="text-sm font-medium">Your estimated cash needed</h3>
            <p className="mt-3 break-words font-display text-4xl font-medium tracking-tight text-[var(--venturu-palette-mint)] tabular-nums sm:text-5xl">
              {money(result.buyerCash)}
            </p>
            <p className="mt-3 text-xs leading-relaxed">
              {currency} · Your part of the purchase and costs, plus cash to
              keep the business running, after the funding you entered.
            </p>
          </div>
          <div className="space-y-6 p-6 sm:p-8">
            <div>
              <p className="text-sm text-muted-foreground">
                Initial monthly loan payments
              </p>
              <p className="mt-1 break-words font-display text-3xl font-medium tabular-nums">
                {money(result.monthly, 2)}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                <GlossaryText
                  terms={terms}
                  text="The monthly total at the start, while both loans are active. This does not include running costs or a final balloon payment."
                />
              </p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                Lender assumption: {input.bankRate}% annually over{" "}
                {input.bankMonths} months.
                {input.sellerAmount > 0 &&
                  ` Seller assumption: ${input.sellerRate}% over ${input.sellerMonths} months${input.sellerInterestOnly ? ", interest only" : ""}.`}
              </p>
            </div>
            <dl className="space-y-3 border-t border-border pt-5 text-sm">
              <div className="flex flex-wrap justify-between gap-2">
                <dt>Total project cost</dt>
                <dd className="tabular-nums">{money(result.projectCost)}</dd>
              </div>
              <div className="flex flex-wrap justify-between gap-2">
                <dt>Lender payment / month</dt>
                <dd className="tabular-nums">{money(bank.monthly, 2)}</dd>
              </div>
              {input.sellerAmount > 0 && (
                <div className="flex flex-wrap justify-between gap-2">
                  <dt>Seller payment / month</dt>
                  <dd className="tabular-nums">{money(seller.monthly, 2)}</dd>
                </div>
              )}
              <div className="flex flex-wrap justify-between gap-2">
                <dt>Interest over both loan terms</dt>
                <dd className="tabular-nums">{money(result.totalInterest)}</dd>
              </div>
            </dl>
            {seller.balloon > 0 && (
              <p className="rounded-lg border border-border bg-muted/40 p-4 text-sm leading-relaxed">
                <strong>Plan for a final payment:</strong> the seller principal
                of {money(seller.balloon)} is still due at month{" "}
                {input.sellerMonths}, in addition to that month’s interest.
              </p>
            )}
            {result.cashGap !== null && (
              <p className="border-t border-border pt-5 text-sm leading-relaxed">
                <strong>{money(Math.abs(result.cashGap))}</strong>{" "}
                {result.cashGap > 0
                  ? "additional cash needed beyond the cash you entered."
                  : "cash remaining after your estimated contribution."}{" "}
                This compares cash only; it does not assess financing
                eligibility.
              </p>
            )}
            {!compact && input.bankAmount > 0 && (
              <p className="border-t border-border pt-5 text-sm leading-relaxed text-muted-foreground">
                If the lender rate were 2 percentage points higher for the whole
                term, combined initial payments would be{" "}
                <strong className="text-foreground">
                  {money(result.stressedMonthly, 2)}/month
                </strong>
                . This is a sensitivity check, not a rate forecast.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      {!compact && (
        <div>
          <h3 className="text-lg font-medium">How the project is funded</h3>
          <svg
            viewBox="0 0 100 4"
            className="my-4 w-full overflow-hidden rounded-full"
            aria-hidden="true"
          >
            {funding.map((item, index) => (
              <rect
                key={item.label}
                x={
                  (funding
                    .slice(0, index)
                    .reduce((sum, part) => sum + part.amount, 0) /
                    result.projectCost) *
                  100
                }
                y="0"
                width={(item.amount / result.projectCost) * 100}
                height="4"
                className={item.color}
              />
            ))}
          </svg>
          <dl className="space-y-3 text-sm">
            {funding.map((item) => (
              <div
                key={item.label}
                className="flex flex-wrap justify-between gap-2"
              >
                <dt>{item.label}</dt>
                <dd className="tabular-nums">{money(item.amount)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
